import * as React from "react";
import { Grid, Box, Button, Typography } from "@mui/material";
import _ from "lodash";
import { Panel, Lollipop, Donut, StackedComposition, AreaCurve } from "../dashboard/NewCharts";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { formatDate } from "../../helpers/UtilityHelper";
import DelayMisDownloadButton from "./DelayMisDownloadButton";
import OrderItemsModal from "../../common/OrderItemsModal";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { apiConstants, post } from "../../common/constants";
import { cellStackSx, primaryTextSx, secondaryTextSx, tertiaryTextSx, awbTextSx } from "../orders/list2/cellStyles";
import { delayChip, routeCell, attemptsDonutData } from "./ctCells";
import AwbLinkCell from "../../common/AwbLinkCell";
import useAddressesByAwb from "../../common/hooks/useAddressesByAwb";
import { FONT, WEIGHT } from "../../styles/foundation/typography";
import { NAVY, BORDER, SUBTLE, WHITE, ERROR, SUCCESS } from "../../styles/foundation/colors";
import { SHADOW } from "../../styles/foundation/effects";

// One colour per delivery zone for the Courier × Zone stacked bars.
const ZONE_PALETTE = ["#3783e7", "#2e9e6b", "#d99a3a", "#c0392b", "#7a4fb0", "#0f766e"];

export default function ForwardDelayed({ data }) {
  const delayedAgingChartData = _(data)
    .groupBy(function (item) {
      if (item.deliveryBreachedAgeingDays >= 7) return "7+";
      return `${item.deliveryBreachedAgeingDays}`;
    })
    .map(function (items, key) {
      return { name: key, value: items.length };
    })
    .orderBy("name", "asc")
    .value();

  const noOfAttemptsChart = _(data)
    .groupBy(function (item) {
      return item.deliveryAttemptCount;
    })
    .map(function (items, key) {
      const totalItems = data.length;
      const itemsGrouped = items.length;
      const n = key === "undefined" || key === "null" || key === "" ? 0 : Number(key);
      // Label the bucket in plain words: the bar counts SHIPMENTS, the label is the attempt count.
      return {
        name: `${n} attempt${n === 1 ? "" : "s"}`,
        attempts: n,
        value: itemsGrouped,
        perc: Math.round((itemsGrouped / totalItems) * 100),
      };
    })
    .orderBy((r) => r.attempts, "asc")
    .value();

  // Courier × Zone: rows = couriers (busiest first), zones as stacked segments,
  // bar length = the courier's total delayed shipments. One colour per zone.
  const zoneLabel = (z) => (z === "REGIONAL" ? "WITHIN STATE" : z);
  const zoneColumns = _.uniq(data.map((d) => d.merchantZone))
    .filter((z) => z && z !== "undefined")
    .map((z, i) => ({ key: z, label: zoneLabel(z), color: ZONE_PALETTE[i % ZONE_PALETTE.length] }));
  const courierZoneMatrix = _(data)
    .groupBy("courierName")
    .map((items, courier) => ({
      name: courier,
      values: _.countBy(items.filter((d) => d.merchantZone && d.merchantZone !== "undefined"), "merchantZone"),
      _total: items.length,
    }))
    .orderBy("_total", "desc")
    .value();

  const paymentModeSplit = _(data)
    .groupBy("paymentMode")
    .map(function (items, key) {
      return { name: key, value: items.length };
    })
    .value();

  const courierWiseSplit = _(data)
    .groupBy("courierName")
    .map(function (items, key) {
      return {
        name: key.replaceAll('Forward', '').replaceAll('_', ' ').replaceAll('FORWARD', '').replaceAll('-', ''),
        value: items.length,
      };
    })
    .orderBy("value", "desc")
    .value();

  const paymentDonut = paymentModeSplit.map((p) => ({ name: p.name, value: p.value, color: /cod/i.test(p.name) ? ERROR : SUCCESS }));
  const totalPay = _.sumBy(paymentModeSplit, "value") || 1;
  const codPerc = Math.round(((_.sumBy(paymentModeSplit.filter((p) => /cod/i.test(p.name)), "value")) / totalPay) * 100);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Panel title="Delayed Shipments Ageing" sub="How many days past SLA each delayed shipment is — the curve traces the falloff so you see where most of your delay backlog sits (order date is used if pickup date is missing).">
          <AreaCurve data={delayedAgingChartData} height={300} color="#e58200" xLabel="Days past SLA" yLabel="Shipments" valueName="Delayed shipments" />
        </Panel>
      </Grid>
      <Grid item xs={12} md={6}>
        <Panel title="Delivery Attempts" sub="Share of delayed shipments by how many delivery attempts the courier has made. Centre shows the total.">
          <Donut data={attemptsDonutData(noOfAttemptsChart)} centerValue={data.length} centerLabel="Shipments" />
        </Panel>
      </Grid>

      <Grid item xs={12} md={6}>
        <Panel title="Payment Mode of Delayed Shipments">
          <Donut data={paymentDonut} centerValue={`${codPerc}%`} centerLabel="COD" />
        </Panel>
      </Grid>
      <Grid item xs={12}>
        <Panel title="Courier Partners for Delayed Shipments">
          <Lollipop data={courierWiseSplit} nameLabel="Courier partner" valueLabel="Delayed shipments" />
        </Panel>
      </Grid>

      <Grid item xs={12}>
        <Panel title="Courier × Zone — Delayed Shipments" sub="Each bar is a courier's total delayed shipments (longest = worst), split by delivery zone. Hover any segment for the exact count and its share of that courier.">
          <StackedComposition rows={courierZoneMatrix} buckets={zoneColumns} absolute />
        </Panel>
      </Grid>

      <Grid item xs={12}>
        <TableData
          data={data}
          title="Delayed Shipments"
          subheader="All shipments in transit / failed-delivery / out-for-delivery / delayed / held / picked-up / pickup-pending / order-placed / out-for-pickup / pickup-failed that have breached the agreed delivery SLA."
        />
      </Grid>
    </Grid>
  );
}

function TableData({ data, title, subheader }) {
  const [sizePerPage, setSizePerPage] = React.useState(20);
  const [page, setPage] = React.useState(0);
  const [orderItems, setOrderItems] = React.useState(null);
  const [openList, setOpenList] = React.useState(false);

  // Current page's rows (client-side paginated). Enrich only these AWBs with pickup/drop
  // addresses for the grid; the MIS download fetches the full set lazily on click.
  const pageData = [...data].splice(page * sizePerPage, sizePerPage);
  const { addrMap } = useAddressesByAwb(pageData.map((r) => r.awbNumber));

  const getItemList = (awb) => {
    const bodyData = {
      includes: "itemList",
      andfilter: [{ field: "awbNumber", operator: "eq", value: awb }],
    };
    post(`${apiConstants.BASE_LOCAL}${apiConstants.ORDER_LISTS}`, bodyData)
      .then((response) => {
        setOpenList(true);
        setOrderItems(response.data.data[0].item_list);
      })
      .catch((err) => {
        setOpenList(false);
        console.error(err);
      });
  };

  const handleClose = () => {
    setOrderItems(null);
    setOpenList(false);
  };

  return (
    <Box sx={{ bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: "10px", boxShadow: SHADOW.card, overflow: "hidden" }}>
      <OrderItemsModal items={orderItems} setOpen={handleClose} openList={openList} />
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, px: 2, py: 1.5, borderBottom: `1px solid ${BORDER}` }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontSize: "11px", fontWeight: WEIGHT.extra, letterSpacing: "0.07em", textTransform: "uppercase", color: SUBTLE }}>{title}</Typography>
          {subheader && <Typography sx={{ fontFamily: FONT, fontSize: "10.5px", color: "#93a0b0", mt: 0.4, maxWidth: 720, lineHeight: 1.4 }}>{subheader}</Typography>}
        </Box>
        <DelayMisDownloadButton
          rows={data}
          filename="Delayed Shipments"
          baseColumns={[
            { label: "Reference", value: "shippingLabelId" },
            { label: "Courier Partner", value: "courierName" },
            { label: "AWB Number", value: "awbNumber" },
            { label: "Order Date", value: (row) => formatDate(row.awbRegisteredDate, "dd MMM, yyyy") },
            { label: "AWB Registered Date", value: (row) => formatDate(row.awbRegisteredDate, "dd MMM, yyyy") },
            { label: "Pickup Date", value: (row) => formatDate(row.pickupDate, "dd MMM, yyyy hh:mm aa") },
            { label: "Edd", value: (row) => formatDate(row.edd, "dd MMM, yyyy hh:mm aa") },
            { label: "Zone", value: "merchantZone" },
            { label: "Delay", value: "deliveryBreachedAgeingDays" },
            { label: "Attempts", value: "totalAttempts" },
          ]}
        />
      </Box>
      <Box sx={{ px: 0 }}>
        <ProzoDataGrid
          columns={[
            {
              field: "order", headerName: "Order Details", width: 190, headerAlign: "left", hideSortIcons: true,
              renderCell: ({ row }) => (
                <Box sx={cellStackSx}>
                  <Typography sx={primaryTextSx}>{row.reference || row.shippingLabelId || "—"}</Typography>
                  {row.shippingLabelId && row.shippingLabelId !== row.reference && <Typography sx={secondaryTextSx}>{row.shippingLabelId}</Typography>}
                  <Typography sx={secondaryTextSx}>{formatDate(row.awbRegisteredDate, "dd MMM, yyyy")}</Typography>
                  <Button sx={{ p: 0, minWidth: 0, justifyContent: "flex-start", fontSize: 11, textTransform: "none" }}
                    startIcon={<VisibilityIcon sx={{ fontSize: "14px !important" }} />} variant="link"
                    onClick={() => getItemList(row.awbNumber)}>View Items</Button>
                </Box>
              ),
            },
            {
              field: "courier", headerName: "Courier Partner", width: 210, headerAlign: "left", hideSortIcons: true,
              renderCell: ({ row }) => (
                <Box sx={cellStackSx}>
                  <AwbLinkCell awb={row.awbNumber} sx={awbTextSx} />
                  <Typography sx={secondaryTextSx}>{addrMap[row.awbNumber]?.courierLabel || row.courierName}</Typography>
                </Box>
              ),
            },
            {
              field: "route", headerName: "Pickup → Drop", width: 250, headerAlign: "left", hideSortIcons: true,
              renderCell: ({ row }) => routeCell(row, addrMap),
            },
            {
              field: "timeline", headerName: "Timeline", width: 210, headerAlign: "left", hideSortIcons: true,
              renderCell: ({ row }) => (
                <Box sx={cellStackSx}>
                  <Typography sx={secondaryTextSx}><Box component="span" sx={{ ...primaryTextSx, fontSize: "inherit" }}>Pickup: </Box>{row.pickupDate ? formatDate(row.pickupDate, "dd MMM, hh:mm aa") : "—"}</Typography>
                  <Typography sx={secondaryTextSx}><Box component="span" sx={{ ...primaryTextSx, fontSize: "inherit" }}>EDD: </Box>{row.estimatedDeliveryDate ? formatDate(row.estimatedDeliveryDate, "dd MMM, hh:mm aa") : "—"}</Typography>
                  {row.maxTat != null && <Typography sx={tertiaryTextSx}>SLA {row.maxTat} days</Typography>}
                </Box>
              ),
            },
            {
              field: "delay", headerName: "Delay", width: 140, headerAlign: "center", align: "center", hideSortIcons: true,
              renderCell: ({ row }) => (
                <Box sx={{ ...cellStackSx, alignItems: "center" }}>
                  {delayChip(row.deliveryBreachedAgeingDays)}
                  <Typography sx={tertiaryTextSx}>{row.deliveryAttemptCount || 0} attempts</Typography>
                </Box>
              ),
            },
            {
              field: "invoice_value", headerName: "Invoice", width: 130, headerAlign: "right", align: "right", hideSortIcons: true,
              renderCell: ({ row }) => <Typography sx={primaryTextSx}>₹{Math.round(addrMap[row.awbNumber]?.invoiceValue ?? row.invoice_value ?? 0)}</Typography>,
            },
          ]}
          autoHeight={true}
          rows={pageData}
          sizePerPage={sizePerPage}
          setSizePerPage={setSizePerPage}
          setPage={setPage}
          page={page}
          pagination={true}
          hideFooterPagination={false}
          filterMode={"server"}
          rowCount={data.length}
          getRowId={(row) => row.id}
        />
      </Box>
    </Box>
  );
}
